import { useState, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '@/services/auth';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';

export function SignupSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email') || 'your email';
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setIsLoading(true);
    try {
      await authService.verifySignupCode(code);
      // Session is active — verify with the backend (creates the default
      // org on first login) and land on the dashboard
      const { user, organizations } = await api.verifyAuth();
      useAuthStore.setState({
        user,
        organizations,
        currentOrganization: organizations[0] ?? null,
        isAuthenticated: true,
        isLoading: false,
      });
      if (organizations[0]) {
        api.setOrganizationId(organizations[0].id);
      }
      navigate('/dashboard');
      return;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid verification code');
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setInfo(null);
    try {
      await authService.resendSignupCode();
      setCode('');
      setInfo('New code sent. Check your email.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="flex justify-center items-center space-x-2">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo.png" alt="" className="h-8 w-8" />
            <img src="/logo-text.png" alt="Statsmatic" className="h-6" />
          </Link>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
          <div className="w-16 h-16 bg-primary-50 border border-primary-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 text-center mb-3">Check your email</h1>
          <p className="text-gray-600 text-center mb-6">
            We've sent a verification code to <strong className="text-gray-900">{email}</strong>.
            Enter it below to activate your account.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}
          {info && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm mb-4">
              {info}
            </div>
          )}

          <form onSubmit={handleVerify} className="space-y-4">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoFocus
              placeholder="Verification code"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center tracking-[0.4em] placeholder:tracking-normal focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <button
              type="submit"
              disabled={isLoading}
              className="block w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white font-semibold rounded-lg transition-colors text-center"
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </button>
          </form>

          <button
            type="button"
            onClick={handleResend}
            disabled={isLoading}
            className="mt-4 w-full text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
          >
            Resend code
          </button>
        </div>
      </div>
    </div>
  );
}

export default SignupSuccess;
